import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useI18n } from "@/i18n/I18nContext";
import { dateCourte } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Ruler, SatelliteDish, Trash2, MapPin, Loader2, FilePlus, Pencil } from "lucide-react";
import { toast } from "sonner";

/**
 * Liste des mesures (rubrique « Mesures »).
 *
 * Une mesure est un relevé de toit sans chiffrage : des couvreurs se servent de
 * l'outil uniquement pour mesurer. Chaque ligne offre « Créer une soumission »,
 * qui ouvre l'atelier de soumission avec la mesure déjà chargée — aucun nouvel
 * appel satellite, donc rien de décompté du forfait.
 */
export default function Mesures() {
  const navigate = useNavigate();
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [deleting, setDeleting] = useState(null);

  const { data: mesures = [], isLoading, refetch } = useQuery({
    queryKey: ["mesures"],
    queryFn: async () => (await api.get("/mesures")).data,
  });

  const remove = async (id) => {
    if (!window.confirm(fr ? "Supprimer cette mesure ?" : "Delete this measurement?")) return;
    setDeleting(id);
    try {
      await api.delete(`/mesures/${id}`);
      toast.success(fr ? "Mesure supprimée" : "Measurement deleted");
      refetch();
    } catch {
      toast.error(fr ? "Échec de la suppression" : "Delete failed");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-2">
              {fr ? "Historique" : "History"}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter">
              {fr ? "Mesures" : "Measurements"}{" "}
              <span className="text-ink-faint font-normal text-2xl">({mesures.length})</span>
            </h1>
          </div>
          <Button onClick={() => navigate("/mesures/nouvelle")} data-testid="mesures-new" className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
            <SatelliteDish className="h-4 w-4 mr-2" /> {fr ? "Nouvelles mesures" : "New measurements"}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand-ink" /></div>
        ) : mesures.length === 0 ? (
          <div className="bg-surface border border-line rounded-md p-12 text-center">
            <Ruler className="h-10 w-10 text-ink-faint mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold mb-2">{fr ? "Aucune mesure" : "No measurements yet"}</h3>
            <p className="text-ink-faint mb-6">
              {fr ? "Mesurez un toit à partir d'une adresse — sans produire de soumission." : "Measure a roof from an address — without producing a quote."}
            </p>
            <Button onClick={() => navigate("/mesures/nouvelle")} className="bg-orange-600 hover:bg-orange-700 rounded-md font-semibold">
              <SatelliteDish className="h-4 w-4 mr-2" /> {fr ? "Commencer" : "Get started"}
            </Button>
          </div>
        ) : (
          <div className="bg-surface border border-line rounded-md overflow-hidden" data-testid="mesures-table">
            {mesures.map((m) => (
              <div key={m.mesure_id} className="flex items-center gap-4 px-5 py-4 border-b border-line last:border-0 hover:bg-hover transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold truncate">
                    <MapPin className="h-3.5 w-3.5 text-brand-ink shrink-0" />
                    <span className="truncate">{m.property_address}</span>
                    {m.manuel && (
                      <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-hover text-ink-faint border border-line">
                        {fr ? "Manuelle" : "Manual"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-ink-faint mt-1">
                    {Math.round(m.footprint_sqft || 0).toLocaleString()} {fr ? "pi² au sol" : "sq ft footprint"}
                    {" · "}{Math.round(m.roof_area_sqft || 0).toLocaleString()} {fr ? "pi² de toit" : "sq ft roof"}
                    {(m.sections || []).length > 1 ? ` · ${m.sections.length} ${fr ? "bâtiments" : "buildings"}` : ""}
                    {" · "}{dateCourte(m.created_at, lang)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {/* Corriger un relevé sans le refaire : on rouvre la carte,
                      les contours et les mesures telles qu'enregistrées, et
                      AUCUN appel satellite n'est décompté du forfait. */}
                  <button
                    onClick={() => navigate(`/mesures/nouvelle?mesure=${m.mesure_id}`)}
                    data-testid={`mesure-modifier-${m.mesure_id}`}
                    title={fr ? "Modifier la mesure" : "Edit measurement"}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-ink-soft rounded-md hover:bg-hover-strong hover:text-ink transition-colors"
                  >
                    <Pencil className="h-4 w-4" /> {fr ? "Modifier" : "Edit"}
                  </button>
                  <button
                    onClick={() => navigate(`/new?mesure=${m.mesure_id}`)}
                    data-testid={`mesure-vers-soumission-${m.mesure_id}`}
                    title={fr ? "Créer une soumission" : "Create a quote"}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-brand-ink rounded-md hover:bg-hover-strong transition-colors"
                  >
                    <FilePlus className="h-4 w-4" /> {fr ? "Créer une soumission" : "Create a quote"}
                  </button>
                  <button onClick={() => remove(m.mesure_id)} data-testid={`mesure-delete-${m.mesure_id}`} title={fr ? "Supprimer" : "Delete"} className="p-2 text-ink-soft hover:text-red-400 rounded-md hover:bg-hover-strong transition-colors">
                    {deleting === m.mesure_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
